import { useEffect, useRef } from 'react';

/**
 * Smart-sync auto-fill decision. Returns true when `incoming` should be written
 * into a field that currently holds `currentValue`. Fills when the field is
 * blank or still holds our own previous auto-fill (`lastFilled`); never
 * overwrites a manual edit, and no-ops when the field already equals `incoming`.
 */
export function shouldAutoFill(
  incoming: string | null,
  currentValue: string,
  lastFilled: string | null,
): boolean {
  if (!incoming) return false;
  if (currentValue === incoming) return false;
  return currentValue.trim() === '' || currentValue === lastFilled;
}

/**
 * Auto-fill a single form field from a verified-ABN value, keeping it in sync as
 * the source changes but never clobbering a manual edit. The effect is keyed on
 * `incoming` only — the current value and `apply` are read through refs — so it
 * runs when a new verification result arrives, not on every keystroke in the
 * field itself.
 */
export function useAutoFillField(
  incoming: string | null,
  currentValue: string,
  apply: (value: string) => void,
): void {
  const lastFilled = useRef<string | null>(null);
  const currentRef = useRef(currentValue);
  currentRef.current = currentValue;
  const applyRef = useRef(apply);
  applyRef.current = apply;

  useEffect(() => {
    if (incoming && shouldAutoFill(incoming, currentRef.current, lastFilled.current)) {
      lastFilled.current = incoming;
      applyRef.current(incoming);
    }
  }, [incoming]);
}
